import * as React from 'react'
import { Typography, IconButton, Toolbar, CssBaseline, Box, Divider, Drawer, Stack, AppBar, Popover, Button, List, ListItemButton, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import { ThemeProvider, createTheme, styled, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { grey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom'

// Translation
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '../../context/ThemeContext';

import { NavItem } from './navItem'
import { navItems, staffNavItems } from './navItemsConfig';

const drawerWidth = 280;

const getSafety24DrawerTheme = (mode) => createTheme({
  palette: {
    mode: mode,
    primary: {
      main: '#dc3545',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#1e3a8a',
      contrastText: '#ffffff',
    },
    background: mode === 'light' ? {
      default: '#f8f9fa',
      paper: '#ffffff',
    } : {
      default: '#0f172a',
      paper: '#1e293b',
    },
    text: mode === 'light' ? {
      primary: '#1e293b',
      secondary: '#64748b',
    } : {
      primary: '#f1f5f9',
      secondary: '#cbd5e1',
    },
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: mode === 'light' 
            ? '1px solid #e2e8f0'
            : '1px solid #334155',
        },
      },
    },
  },
});

function renderNavItems({ items = [], pathname }) {
  const children = items.reduce((acc, curr) => {
    const { key, ...item } = curr;

    acc.push(<NavItem key={key} pathname={pathname} {...item} />);

    return acc;
  }, []);

  return (
    <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Stack>
  );
}


export default function DrawerNav({ backToDashboard, activeComponent }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [langAnchorEl, setLangAnchorEl] = React.useState(null)
  const [hasBackToDashboard, setHasBackToDashboard] = React.useState(false)
  const [selectedLang, setSelectedLang] = React.useState('')
  const [fullName, setName] = React.useState("")
  const [isStaff, setIsStaff] = React.useState(false)

  // Theme context
  const { mode, toggleTheme } = React.useContext(ThemeContext);
  const theme = useTheme();

    // Translation
  const { t, i18n } = useTranslation()

  const language_list = [
    {"value": "pl", "label": "Polski", "icon": <img src="/polish.png" height={32} width={32} alt='polish'/>},
    {"value": "en", "label": "English", "icon": <img src="/english.png" height={32} width={32} alt='english'/>}
  ]

  const language_icons = {
    "pl": <img src="/polish.png" height={32} width={32} alt='polish'/>,
    "en": <img src="/english.png" height={32} width={32} alt='english'/>
  }

  React.useEffect(() => {
    try {
      setHasBackToDashboard(backToDashboard)
      waitForLang()
      setName(localStorage.getItem("name"))
      if (localStorage.getItem("is_staff") === 'false') {
        setIsStaff(false)
      } else {
        setIsStaff(true)
      }
    } catch (err) {
  
    }
  }, [])

  async function waitForLang() {
    let temp = await i18n.resolvedLanguage
    setSelectedLang(temp)
  }

  const navigate = useNavigate()

  const popoverOpen = Boolean(anchorEl)
  const popoverId = popoverOpen ? 'user-popover' : undefined

  const langPopoverOpen = Boolean(langAnchorEl)
  const langPopoverId = popoverOpen ? 'lang-popover' : undefined

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const handleLangPopoverOpen = (event) => {
    setLangAnchorEl(event.currentTarget)
  }

  const handleLangPopoverClose = () => {
    setLangAnchorEl(null)
  }

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleBackToDashboardClick = () => {
    navigate("/")
  }

  const ColorButton = styled(Button)(({ theme }) => ({
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' ? grey[200] : 'rgba(255, 255, 255, 0.08)',
    },
  }));

  function Logout() {
    localStorage.clear()
    navigate("/login")
  }
  
  const drawer = (
    <div>
      <Toolbar>
        <Box alt="logo" /*component="img"  src={'/assets/logo1.svg'}*/ width={230} sx={{ marginY: 2.5, textAlign: 'center' }}>
          {/* <Typography sx={{ fontSize: "3rem", letterSpacing: 3 }} fontWeight='bold'>Safety<span style={{ color: "#4caf50" }}>24</span></Typography> */}
          {/* <img src="/24SAFETYlogo.png" alt="logo" width={230} /> */}
          <img src="/Logo24Safety.png" alt="logo" width={150} />
        </Box>
      </Toolbar>
      <Divider />
      <Box component="nav" sx={{ flex: '1 1 auto', p: '12px' }}>
        {renderNavItems({ items: navItems, pathname: activeComponent })}
        <Divider sx={{ my: 1 }}/>
        {isStaff ? renderNavItems({ items: staffNavItems, pathname: activeComponent }) : ''}
      </Box>
    </div>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          ml: { lg: `${drawerWidth}px` },
          boxShadow: 'none',
          backgroundColor: mode === 'light' ? '#fff' : theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ bgcolor: 'background.paper' }}>
          <IconButton
            color="black"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { lg: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Button startIcon={<ArrowBackIcon />} width={200} size='small' variant='outlined' sx={{ width: 225, display: hasBackToDashboard ? '' : 'none', mr: 2 }} onClick={handleBackToDashboardClick}>
            {t("layout.back_to_dashboard_button")}
          </Button>
          <Box height={28} >
            <img src="/24SAFETYlogo.png" alt="logo" height={28}/>
          </Box>
          <Stack
            width="100%"
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            alignItems="center"
          >
            <IconButton 
              onClick={toggleTheme} 
              sx={{ 
                color: mode === 'light' ? '#1e293b' : '#f1f5f9',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'rotate(180deg)',
                }
              }}
            >
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <IconButton aria-describedby={langPopoverId} onClick={handleLangPopoverOpen}>
              {selectedLang ? language_icons[selectedLang] : '' }
            </IconButton>
            <Popover
              id={langPopoverId}
              open={langPopoverOpen}
              anchorEl={langAnchorEl}
              onClose={handleLangPopoverClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{
                sx: {
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: theme.palette.mode === 'light' 
                    ? '0 4px 20px rgba(0, 0, 0, 0.1)' 
                    : '0 4px 20px rgba(0, 0, 0, 0.5)',
                }
              }}
            >
              <List>
                {language_list.map((item) =>
                  <ListItem 
                    key={item.value}
                    disablePadding 
                    sx={{ 
                      bgcolor: selectedLang === item.value 
                        ? (theme.palette.mode === 'light' ? '#e0e0e0' : 'rgba(255, 255, 255, 0.12)')
                        : 'transparent',
                      '&:hover': { 
                        bgcolor: selectedLang === item.value 
                          ? (theme.palette.mode === 'light' ? '#e0e0e0' : 'rgba(255, 255, 255, 0.12)')
                          : (theme.palette.mode === 'light' ? '#eeeeee' : 'rgba(255, 255, 255, 0.08)')
                      } 
                    }}
                  >
                    <ListItemButton onClick={() => {
                      i18n.changeLanguage(item.value)
                      setSelectedLang(item.value)
                      handleLangPopoverClose()
                    }}>
                      <ListItemIcon>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.label}
                        primaryTypographyProps={{
                          sx: { color: 'text.primary' }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Popover>
            <IconButton aria-describedby={popoverId} onClick={handlePopoverOpen}>
              <AccountCircleIcon fontSize="large" color='primary' />
            </IconButton>
            <Popover
              id={popoverId}
              open={popoverOpen}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{
                sx: {
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: theme.palette.mode === 'light' 
                    ? '0 4px 20px rgba(0, 0, 0, 0.1)' 
                    : '0 4px 20px rgba(0, 0, 0, 0.5)',
                }
              }}
            >
              <Stack
                direction="column"
                sx={{
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                <Typography sx={{ paddingX: 2, paddingTop: 2, color: 'text.primary' }} variant='body' fontWeight='500'>{fullName}</Typography>
                <Typography sx={{ paddingX: 2, color: 'text.secondary' }} variant='caption'>
                  {Boolean(isStaff) ? t("layout.admin") : t("layout.user")}
                </Typography>
                <Divider width="100%" sx={{ marginY: 1 }} />
                <Box width="100%" sx={{ paddingX: 1 }}>
                  <ColorButton
                    variant='text'
                    color="secondary"
                    startIcon={<SettingsIcon />}
                    fullWidth
                    sx={{ marginBottom: .5, borderRadius: 1, letterSpacing: 1, paddingLeft: 1, justifyContent: "flex-start" }}
                  >
                    {t("layout.settings_text")}
                  </ColorButton>
                </Box>
                <Box width="100%" sx={{ paddingX: 1 }}>
                  <ColorButton
                    variant='text'
                    color="secondary"
                    startIcon={<PersonIcon />}
                    fullWidth
                    sx={{ marginBottom: .5, borderRadius: 1, letterSpacing: 1, paddingLeft: 1, justifyContent: "flex-start" }}
                  >
                    {t("layout.account_text")}
                  </ColorButton>
                </Box>
                <Box width="100%" sx={{ paddingX: 1 }}>
                  <ColorButton
                    variant='text'
                    color="secondary"
                    startIcon={<LogoutOutlinedIcon />}
                    fullWidth
                    sx={{ marginBottom: 1, borderRadius: 1, letterSpacing: 1, paddingLeft: 1, justifyContent: "flex-start" }}
                    onClick={Logout}
                  >
                    {t("layout.logout_text")}
                  </ColorButton>
                </Box>
              </Stack>
            </Popover>
          </Stack>
        </Toolbar>
      </AppBar>
      <Box sx={{
        display: { s: 'none', lg: 'flex' },
        flexDirection: 'column',
        height: '100%',
        left: 0,
        maxWidth: '100%',
        position: 'fixed',
        scrollbarWidth: 'none',
        top: 0,
      }}>
        <CssBaseline />
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          aria-label="mailbox folders"
        >
          <ThemeProvider theme={getSafety24DrawerTheme(mode)}>
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onTransitionEnd={handleDrawerTransitionEnd}
              onClose={handleDrawerClose}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: 'block', sm: 'block', md: 'block', lg: 'none' },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: drawerWidth,
                },
              }}
            >
              {drawer}
            </Drawer>
            <Drawer
              variant="permanent"
              sx={{
                display: { xs:'none', sm:'none', md: 'none', lg: 'block' },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: drawerWidth,
                },
              }}
              open
            >
              {drawer}
            </Drawer>
          </ThemeProvider>
        </Box>
        {/* <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          {props.children}
        </Box> */}
      </Box>
    </>
  )
}